import React, { useState } from 'react';
import ApiService from '../services/api';

const HomePage = ({ onNavigate, user, onShowAuth }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = async (file) => {
    // Check if user is authenticated
    if (!user) {
      onShowAuth();
      return;
    }

    // Validate file type and size
    const allowedTypes = ['application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 10MB.');
      return;
    }

    setError(null);
    setUploadedFile(file);
    setUploadProgress(0);
    
    try {
      // First test backend connectivity
      console.log('Testing backend connectivity...');
      setUploadProgress(10);
      
      const statusCheck = await ApiService.checkStatus();
      console.log('Backend status check:', statusCheck);
      
      if (!statusCheck || !statusCheck.online) {
        throw new Error('Backend service is not available. Please try again later.');
      }
      
      setUploadProgress(25);
      console.log('Backend is reachable, starting upload...');

      const formData = new FormData();
      formData.append('file', file);

      // Upload progress simulation
      for (let i = 30; i <= 50; i += 10) {
        setTimeout(() => setUploadProgress(i), (i - 30) * 50);
      }

      const response = await ApiService.uploadPdf(formData);
      
      if (!response.pdf_id) {
        throw new Error('Invalid response from server');
      }

      // Continue progress simulation
      for (let i = 60; i <= 100; i += 10) {
        setTimeout(() => setUploadProgress(i), (i - 50) * 20);
      }

      setIsAnalyzing(true);
      const results = await ApiService.getStatement(response.pdf_id);
      setIsAnalyzing(false);
      // Handle the analysis results as needed
      console.log('Analysis results:', results);
      
    } catch (error) {
      console.error('Error processing file:', error);
      setError(`Upload failed: ${error.message}`);
      setUploadProgress(0);
      setIsAnalyzing(false);
      setUploadedFile(null);
    }
  };

  const faqItems = [
    { question: 'Lorem ipsum?', answer: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.' },
    { question: 'Lorem ipsum?', answer: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.' },
    { question: 'Lorem ipsum?', answer: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.' },
    { question: 'Lorem ipsum?', answer: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.' },
    { question: 'Lorem ipsum?', answer: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.' },
  ];

  const valueCards = [
    { title: 'Value #1', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed et aliquet orci.' },
    { title: 'Value #2', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed et aliquet orci.' },
    { title: 'Value #3', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed et aliquet orci.' },
  ];

  const featureCards = [
    { 
      title: 'Instant, Free Audits', 
      description: 'Upload your statement and get a full breakdown in seconds - clear, fast, and completely free.',
      icon: 'lightning'
    },
    { 
      title: 'Markup & Margin Analysis', 
      description: 'We highlight your processor\'s markups and profit margins, then compare your rates against other business owners like you.',
      icon: 'chart'
    },
    { 
      title: 'Negotiation Power', 
      description: 'Use RateIQ\'s benchmarks to negotiate confidently and secure the best rate on the market.',
      icon: 'handshake'
    },
  ];

  const blogPosts = [
    { title: 'This is going to go here', date: 'January 1', description: 'Lorem ipsum' },
    { title: 'This is going to go here', date: 'January 2', description: 'Lorem ipsum' },
    { title: 'This is going to go here', date: 'January 3', description: 'Lorem ipsum' },
  ];

  return (
    <div className="min-h-screen bg-[#00162C] text-white">
      {/* Hero Section */}
      <section className="relative py-28 px-6 sm:px-8 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Bright Spots */}
          <div className="absolute -top-20 -left-20 w-80 h-96 bg-white/20 rounded-full blur-[80px]"></div>
          <div className="absolute -top-20 -right-20 w-80 h-96 bg-white/20 rounded-full blur-[80px]"></div>
          <div className="text-center relative z-10 py-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 max-w-3xl mx-auto">
              Your Secret Weapon Against Payment Processors
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6 max-w-4xl mx-auto">
              <span className="block bg-gradient-to-r from-[#0AB1B5] via-[#6DC066] to-[#7DF1EC] bg-clip-text text-transparent">
                Payment processors profit off your confusion. We put the power back in your hands.
              </span>
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
              Payment processors want you to believe high fees are inevitable. They're not.
              RateIQ scans your credit card processing statement, uncovers the hidden costs, and shows
              you how much you could be saving compared to the best rates on the market.
            </p>
            <button className="bg-[#DBE46D] text-rateiq-darkest-blue px-8 py-3 rounded font-semibold hover:bg-[#DBE46D]/90 transition-colors">
              Upload Statement
            </button>
          </div>
        </div>
      </section>

      {/* Upload Section */}
      <section className="py-12 px-6 sm:px-8 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Decode your statement for free</h2>
          </div>
          
          {error && (
            <div className="mb-8 bg-red-400 bg-opacity-20 border border-red-400 text-red-100 rounded-lg p-4">
              {error}
            </div>
          )}

          <div
            className={`bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-12 text-center transition-all duration-300 ${
              isDragging ? 'bg-white/10 border-[#DBE46D]/50' : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="max-w-md mx-auto">
              {uploadProgress > 0 ? (
                <div className="space-y-4">
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div
                      className="bg-[#DBE46D] h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-gray-300">
                    {isAnalyzing ? 'Analyzing your statement...' : 'Uploading your statement...'}
                  </p>
                  {uploadedFile && (
                    <p className="text-sm text-gray-400">{uploadedFile.name}</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-[#DBE46D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-white text-lg mb-2">
                    Drag & drop files or <label htmlFor="fileInput" className="text-[#DBE46D] cursor-pointer hover:underline">Browse</label>
                  </p>
                  <p className="text-gray-400 text-sm mb-6">
                    Supported formats: PDF only
                  </p>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileInputChange}
                    className="hidden"
                    id="fileInput"
                  />
                  <label
                    htmlFor="fileInput"
                    className="inline-block w-full cursor-pointer absolute inset-0"
                  >
                    <span className="sr-only">Choose file</span>
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Decorative spot */}
        <div className="absolute -right-20 top-1/2 w-64 h-64 bg-white/20 rounded-full blur-[80px] -translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl font-bold text-center mb-6">Our Values</h2>
          <p className="text-gray-300 text-center max-w-2xl mx-auto mb-12 text-lg leading-relaxed">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valueCards.map((value, index) => (
              <div key={index} className="relative rounded-lg px-6 py-10 border border-white/25 overflow-hidden group">
                {/* Gradient background */}
                <div className="absolute inset-0 bg-white/10 backdrop-blur-md"></div>
                {/* Card content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-[#1082A9]/35 rounded-full mb-6 flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{value.title}</h3>
                  <p className="text-white/90">{value.description}</p>
                </div>
                {/* Hover effect */}
                <div className="absolute inset-0 bg-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-md"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Left side - Mockup/Visual */}
            <div className="flex justify-center">
              <div className="relative rounded-2xl p-8 shadow-2xl w-96 h-[32rem] border border-white/25 overflow-hidden">
                {/* Glass background */}
                <div className="absolute inset-0 bg-white/10 backdrop-blur-md"></div>
              </div>
            </div>
            
            {/* Feature cards - Vertical layout */}
            <div className="space-y-12">
              {featureCards.map((feature, index) => (
                <div key={index} className="text-left">
                  <h3 className="text-2xl md:text-3xl font-medium text-white mb-4">{feature.title}</h3>
                  <p className="text-gray-300 text-lg leading-relaxed max-w-3xl">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-700 pb-4">
                <button 
                  className="w-full text-left flex justify-between items-center py-4"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                >
                  <span className="text-lg font-medium">{item.question}</span>
                  <svg 
                    className="w-6 h-6 transition-transform duration-200" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    {openFaqIndex === index ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M20 12H4" />
                    )}
                  </svg>
                </button>
                {openFaqIndex === index && (
                  <div className="mt-2 text-gray-300 pb-4 transition-all duration-200">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-12 px-4">
            <h2 className="text-3xl font-bold">Latest blog</h2>
            <button className="bg-white hover:bg-white/90 text-[#00162C] px-6 py-2 rounded-lg transition-colors duration-200 font-medium">
              Browse all
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
            {blogPosts.map((post, index) => (
              <div key={index} className="group cursor-pointer">
                {/* Image placeholder */}
                <div className="aspect-video bg-white rounded-lg mb-4 transition-transform duration-200 group-hover:scale-[1.02]"></div>
                {/* Content */}
                <div className="space-y-3">
                  <span className="text-gray-400 text-sm block">{post.date}</span>
                  <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-green-400 transition-colors duration-200">{post.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-6 sm:px-8 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 rounded-xl p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                This should be a nice<br />short and concise header
              </h2>
              <button className="bg-white hover:bg-gray-100 text-gray-900 font-medium px-8 py-3 rounded transition-colors duration-200">
                Browse all
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;